import type { AdminLoginStep, AdminProfile, AuthResult } from '@artiman/shared';
import { API_URL } from '@/config';

/** Error shape of the API (`{ code, message, details, traceId }`). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

const CSRF = { 'x-artiman-csrf': '1' };

/** Access token kept in memory only; the refresh token is an httpOnly cookie scoped to /api/v1/admin/auth. */
let accessToken: string | null = null;
let refreshing: Promise<AuthResult<AdminProfile> | null> | null = null;
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(fn: () => void) {
  onSessionLost = fn;
}

async function raw<T>(path: string, init: RequestInit & { json?: unknown; auth?: boolean } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.json !== undefined) headers.set('content-type', 'application/json');
  if (init.auth && accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  let res: Response;
  try {
    res = await fetch(API_URL + path, {
      ...init, headers, credentials: 'include',
      body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
    });
  } catch {
    throw new ApiError(0, 'network_error', 'ارتباط با سرور برقرار نشد.');
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body?.code ?? 'error', body?.message ?? `خطای ${res.status}`, body?.details);
  return body as T;
}

/** Single-flight refresh; retries once when another tab rotated the cookie at the same moment. */
export function refreshSession(): Promise<AuthResult<AdminProfile> | null> {
  refreshing ??= (async () => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await raw<AuthResult<AdminProfile>>('/api/v1/admin/auth/refresh', { method: 'POST', headers: CSRF });
        accessToken = r.accessToken;
        return r;
      } catch (e) {
        if (e instanceof ApiError && e.code === 'refresh_race' && attempt === 0) continue;
        accessToken = null;
        return null;
      }
    }
    return null;
  })().finally(() => { refreshing = null; });
  return refreshing;
}

/** Authenticated call: refreshes once on 401, then reports the lost session. */
export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  if (!accessToken && !(await refreshSession())) {
    onSessionLost?.();
    throw new ApiError(401, 'unauthorized', 'نشست شما پایان یافته است.');
  }
  try {
    return await raw<T>(path, { ...init, auth: true });
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) throw e;
    if (!(await refreshSession())) {
      onSessionLost?.();
      throw e;
    }
    return raw<T>(path, { ...init, auth: true });
  }
}

export const authApi = {
  login: (email: string, password: string) =>
    raw<AdminLoginStep>('/api/v1/admin/auth/login', { method: 'POST', json: { email, password } }),
  totpSetup: (challenge: string) =>
    raw<{ secret: string; otpauthUrl: string }>('/api/v1/admin/auth/totp/setup', { method: 'POST', json: { challenge } }),
  async totpVerify(challenge: string, code: string) {
    const r = await raw<AuthResult<AdminProfile>>('/api/v1/admin/auth/totp/verify', { method: 'POST', json: { challenge, code } });
    accessToken = r.accessToken;
    return r;
  },
  async logout() {
    try {
      await raw('/api/v1/admin/auth/logout', { method: 'POST', headers: CSRF });
    } finally {
      accessToken = null;
    }
  },
  changePassword: (current: string, next: string) => api<void>('/api/v1/admin/auth/password', { method: 'POST', json: { current, next } }),
};

/** Authenticated download (e.g. a CSV export) saved under `filename`. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  if (!accessToken && !(await refreshSession())) throw new ApiError(401, 'unauthorized', 'نشست شما پایان یافته است.');
  const res = await fetch(API_URL + path, { headers: { authorization: `Bearer ${accessToken}` }, credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, 'download_failed', 'دریافت فایل انجام نشد.');
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Authenticated private file (KYC document) as an object URL, for an <img> or a new tab. The caller
 * revokes it. Never cached: the API answers `no-store`.
 */
export async function fileUrl(path: string): Promise<{ url: string; type: string }> {
  if (!accessToken && !(await refreshSession())) throw new ApiError(401, 'unauthorized', 'نشست شما پایان یافته است.');
  let res = await fetch(API_URL + path, { headers: { authorization: `Bearer ${accessToken}` }, credentials: 'include' });
  if (res.status === 401 && (await refreshSession())) res = await fetch(API_URL + path, { headers: { authorization: `Bearer ${accessToken}` }, credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, 'download_failed', 'دریافت فایل انجام نشد.');
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

/** Public (unauthenticated) API, e.g. live prices. */
export const publicApi = <T>(path: string) => raw<T>(path);

/** True when the readable hint cookie says an admin session probably exists. */
export const hasSessionHint = () => document.cookie.split('; ').some(c => c === 'artiman_admin=1');
